import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { Confetti } from '../ui/bits.tsx';
import { asset } from '../engine/assets.ts';
import { Icon } from '../ui/Icon.tsx';
import { PaymentMarks } from '../ui/PaymentMarks.tsx';
import { CheckoutDialog, CHECKOUT_FRAME, scrollCheckoutToTop } from '../ui/CheckoutDialog.tsx';
import { openCheckout, CheckoutUnavailable } from '../engine/paddle.ts';
import { captureAttribution, leadId, metaCookies, sessionId } from '../engine/session.ts';
import { haptic, hapticDone } from '../engine/haptics.ts';
import { CEL, CORAL_DEEP, CREAM, GRAD, GREEN_BG, GREEN_FG, GREEN_TEXT, INK, LINE, MUTED, ORANGE, PAPER, SHADOW_CARD, SHADOW_GLOW, SUN, TIMER_BG, TIMER_FILL, VIOLET, YELLOW } from '../ui/theme.ts';

type Pay = 'idle' | 'processing' | 'error' | 'success';

/**
 * Timer card · plan cards (values from settings.paddle.plans) · disclosure per
 * plan · included list · trust row · payment marks · social line · links.
 * States: idle / processing / error / success.
 */
export function PlanPage({ step, content, ctx }: StepProps) {
  const shared = ctx.shared.planPage;
  const settings = ctx.config.settings;
  const total = step.timerSeconds ?? settings.timerMinutes * 60;
  const [left, setLeft] = useState(total);
  const [pay, setPay] = useState<Pay>('idle');
  const [unavailable, setUnavailable] = useState(false);
  const [dialog, setDialog] = useState(false);
  // Paddle's form is on screen: the visitor is typing, nothing is being confirmed yet
  const [formOpen, setFormOpen] = useState(false);
  const expired = useRef(false);
  const fold = useRef<HTMLDivElement | null>(null);
  const plan = ctx.selectedPlan;
  // per-card copy is templated per plan, so it is read unrendered and resolved
  // against that card's values (the shared block is already rendered against
  // the *selected* plan, which is only right for the CTA disclosure).
  const rawPlans = ctx.config.content.shared.planPage.plans;
  const planContent = rawPlans.find((p) => p.id === plan.id);
  // the longest term is the "best value" card: it gets the violet accent
  const bestId = [...ctx.plans].sort((a, b) => b.weeks - a.weeks)[0]?.id;
  const disclosure = ctx.renderPlan(planContent?.disclosure ?? shared.disclosureFallback, plan);

  useEffect(() => {
    ctx.event('plan_view', { preselected: step.preselected });
    const id = setInterval(() => {
      setLeft((s) => {
        if (s <= 1 && !expired.current) {
          expired.current = true;
          ctx.event('timer_expired');
        }
        return Math.max(0, s - 1);
      });
    }, 1000);
    return () => clearInterval(id);
  }, [step.id]);

  const start = async () => {
    if (pay === 'processing') return;
    if (pay === 'success') return;
    setPay('processing');
    // the dialog has to exist before Paddle looks for its mount point
    setDialog(true);
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const event = ctx.event('checkout_start', {
      plan_id: plan.id,
      price_id: plan.priceId,
      value: plan.amount / 100,
      currency: plan.currency,
    });

    try {
      await openCheckout(
        settings,
        {
          priceId: plan.priceId,
          email: ctx.leadEmail ?? '',
          lang: ctx.config.lang,
          inlineTarget: CHECKOUT_FRAME,
          customData: {
            // the app attributes a purchase by this: anything Paddle sends it
            // without source "funnel" came from its own pricing page
            source: 'funnel',
            // first-touch attribution and the Meta cookies travel with the
            // transaction, so the webhook can send a fully matched Purchase
            session_id: sessionId(),
            ...pick(captureAttribution(), ['angle', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid']),
            ...metaCookies(),
            // the app creates the account from this when the buyer has none;
            // it can fetch the address from Paddle, but sending it saves a call
            email: ctx.leadEmail ?? undefined,
            lead_id: leadId(),
            sid: sessionId(),
            funnel_code: ctx.config.funnel.code,
            lang: ctx.config.lang,
            plan_id: plan.id,
            archetype: ctx.archetype.id,
            projection_factor: ctx.projection.factor,
            checkout_event_id: event.event_id,
          },
        },
        {
          onLoaded: () => {
            setFormOpen(true);
            ctx.event('checkout_open', { plan_id: plan.id });
          },
          onCompleted: (transactionId) => {
            setFormOpen(false);
            setDialog(false);
            setPay('success');
            ctx.event('purchase', {
              // Paddle's transaction id is the Meta event_id on both sides:
              // this browser event and the webhook's server event dedupe to one
              event_id: transactionId ?? undefined,
              transaction_id: transactionId,
              plan_id: plan.id,
              value: plan.amount / 100,
              currency: plan.currency,
              is_intro: plan.hasIntro,
            });
            // no redirect: the app emails the login link once the payment is approved
          },
          onClosed: () => {
            setFormOpen(false);
            setDialog(false);
            setPay((p) => (p === 'success' ? p : 'idle'));
            ctx.event('checkout_abandoned', { plan_id: plan.id });
          },
          onPaymentFailed: () => {
            hapticDone();
            scrollCheckoutToTop();
            ctx.event('checkout_payment_failed', { plan_id: plan.id });
          },
          onError: (code) => {
            setFormOpen(false);
            setDialog(false);
            setPay('error');
            ctx.event('checkout_error', { error_code: code, plan_id: plan.id });
          },
        },
      );
    } catch (e) {
      setFormOpen(false);
      setDialog(false);
      const notConfigured = e instanceof CheckoutUnavailable;
      setUnavailable(notConfigured);
      setPay('error');
      ctx.event('checkout_error', { error_code: notConfigured ? 'not_configured' : 'open_failed', plan_id: plan.id });
    }
  };

  useEffect(() => {
    if (pay === 'success') {
      ctx.setCta({ label: null });
      return;
    }
    ctx.setCta({
      label: pay === 'processing' ? (formOpen ? (shared.formOpen ?? 'Enter your card details above') : shared.processing) : pay === 'error' ? shared.retry : (content.cta ?? shared.cta),
      icon: pay === 'processing' ? (formOpen ? 'arrow-up' : 'circle-notch') : content.ctaIcon,
      spinner: pay === 'processing' && !formOpen,
      disabled: pay === 'processing',
      large: true,
      caption: disclosure,
      onPress: start,
    });
  }, [step.id, pay, formOpen, plan.id, disclosure]);


  const mm = String(Math.floor(left / 60)).padStart(2, '0');
  const ss = String(Math.floor(left % 60)).padStart(2, '0');

  if (pay === 'success') {
    return (
      <>
        <div
          style={{
            position: 'relative',
            borderRadius: 28,
            background: CEL,
            color: '#fff',
            padding: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            alignItems: 'center',
            textAlign: 'center',
            animation: 'fk-pop .5s ease-out',
            overflow: 'hidden',
            marginTop: 20,
          }}
        >
          <Confetti />
          <Icon name="confetti" size={44} />
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.1 }}>{shared.success.title}</div>
          <p style={{ margin: 0, fontSize: 15, opacity: 0.95 }}>{shared.success.body.replace('{email}', ctx.leadEmail ?? 'your inbox')}</p>
          <Icon name="envelope-simple" size={30} />
        </div>
      </>
    );
  }

  return (
    <>
      {dialog ? (
        <CheckoutDialog
          title={ctx.shared.checkout.dialogTitle ?? ctx.shared.checkout.title}
          onClose={() => {
            setFormOpen(false);
            setDialog(false);
            setPay('idle');
            ctx.event('checkout_abandoned', { plan_id: plan.id });
          }}
        />
      ) : null}

      {/* ---- first screen: timer, the three plans, secure checkout — exactly one
          fold tall, so nothing below shows until the visitor scrolls ---- */}
      <div ref={fold} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--fk-gap)', }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 18, background: TIMER_BG, color: '#fff', boxShadow: '0 14px 36px rgba(31,27,45,.25)', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, background: TIMER_FILL, width: `${Math.round((left / total) * 100)}%`, transition: 'width 1s linear' }} />
        <span style={{ position: 'relative', width: 40, height: 40, flex: 'none', borderRadius: 12, background: GRAD, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', animation: left > 0 ? 'fk-pulse 1.6s ease-out infinite' : 'none' }}>
          <Icon name="timer" size={22} />
        </span>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <div className="fk-copy" style={{ fontSize: 13, fontWeight: 900, letterSpacing: '.01em', textTransform: 'uppercase', lineHeight: 1.15 }}>
            {left > 0 ? shared.timer.title : (ctx.shared.ui?.stillReserved ?? 'Still reserved for you')}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 600, opacity: 0.85, lineHeight: 1.25, marginTop: 2 }}>{shared.timer.sub}</div>
        </div>
        <div style={{ position: 'relative', flex: 'none', display: 'flex', gap: 3, alignItems: 'center', fontVariantNumeric: 'tabular-nums' }}>
          <span style={digit}>{mm}</span>
          <span style={{ fontSize: 20, fontWeight: 800, opacity: 0.7 }}>:</span>
          <span style={digit}>{ss}</span>
        </div>
      </div>

      <div role="radiogroup" aria-label="Choose a plan" style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 2 }}>
        {ctx.plans.map((p) => {
          const c = rawPlans.find((x) => x.id === p.id);
          if (!c) return null;
          const on = p.id === plan.id;
          const best = p.id === bestId;
          const accent = best ? VIOLET : ORANGE;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={pay === 'processing'}
              onClick={() => {
                haptic();
                ctx.selectPlan(p.id);
                if (pay === 'error') setPay('idle');
                ctx.event('plan_select', { plan_id: p.id });
              }}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 70,
                padding: '10px 12px',
                borderRadius: 20,
                textAlign: 'left',
                cursor: pay === 'processing' ? 'default' : 'pointer',
                font: 'inherit',
                color: INK,
                background: on ? CREAM : PAPER,
                border: `2px solid ${on ? accent : LINE}`,
                boxShadow: on ? (best ? `0 10px 30px ${VIOLET}40` : SHADOW_GLOW) : 'none',
                opacity: pay === 'processing' ? 0.6 : 1,
              }}
            >
              <span style={{ width: 24, height: 24, flex: 'none', borderRadius: '50%', border: `2px solid ${on ? 'transparent' : LINE}`, background: on ? (best ? VIOLET : GRAD) : PAPER, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: on ? '#fff' : 'transparent' }} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 800, fontSize: 18, lineHeight: 1.15 }}>{ctx.renderPlan(c.name, p)}</span>
                <span style={{ display: 'block', fontSize: 12, color: MUTED, fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>{ctx.renderPlan(c.cadence, p)}</span>
              </span>
              <span style={{ textAlign: 'right', flex: 'none' }}>
                {p.anchor ? <span style={{ display: 'block', fontSize: 12, color: MUTED, textDecoration: 'line-through', fontWeight: 600 }}>{p.anchor}</span> : null}
                <span style={{ display: 'block', fontWeight: 800, fontSize: 28, letterSpacing: '-.03em', lineHeight: 1 }}>
                  {p.major}
                  <span style={{ fontSize: 15 }}>{p.cents}</span>
                </span>
                <span style={{ display: 'block', fontSize: 12, color: MUTED, fontWeight: 700 }}>{ctx.renderPlan(c.per, p)}</span>
              </span>
              {c.badge ? (
                <span
                  style={{
                    position: 'absolute',
                    top: -11,
                    left: 16,
                    padding: '4px 10px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 800,
                    background: best ? VIOLET : p.id === settings.paddle.baselinePlan ? GRAD : CREAM,
                    color: best || p.id === settings.paddle.baselinePlan ? '#fff' : INK,
                  }}
                >
                  {ctx.renderPlan(c.badge, p)}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {pay === 'error' ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 14px', borderRadius: 16, background: CREAM, fontSize: 13, fontWeight: 700 }}>
          <Icon name="warning-circle" size={20} color={CORAL_DEEP} style={{ flex: 'none' }} />
          <span>{unavailable ? (shared.unavailable ?? 'Checkout is not available yet. Nothing was charged.') : shared.error}</span>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
        <PaymentMarks compact />
        {shared.secure ? (
          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: MUTED, fontWeight: 700 }}>
            <Icon name="lock-key" size={15} color={ORANGE} />
            {shared.secure}
          </div>
        ) : null}
        {ctx.leadEmail ? (
          <div style={{ fontSize: 12.5, color: MUTED, fontWeight: 600 }}>
            {shared.continuingAs.replace('{email}', '')}
            <b style={{ color: INK }}>{ctx.leadEmail}</b>
          </div>
        ) : null}
      </div>
      </div>

      {/* ---- below the fold: proof, what you get, how it works, guarantee, FAQ ---- */}
      {shared.results?.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${shared.results.length}, 1fr)`, gap: 8, marginTop: 8 }}>
          {shared.results.map((r, i) => (
            <div key={r.caption} style={{ padding: '12px 8px', borderRadius: 18, background: i === 1 ? SUN : CREAM, textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1 }}>{r.value}</div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: i === 1 ? INK : MUTED, marginTop: 4 }}>{r.caption}</div>
            </div>
          ))}
        </div>
      ) : null}

      {shared.proofTitle && ctx.shared.testimonials.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={sectionTitle}>{shared.proofTitle}</div>
          {ctx.shared.testimonials.map((t) => (
            <div key={t.name} style={{ padding: 14, borderRadius: 20, background: PAPER, boxShadow: SHADOW_CARD, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ width: 44, height: 44, flex: 'none', borderRadius: '50%', backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: `url(${asset(t.avatar)})` }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'inline-flex', gap: 1, color: YELLOW, marginBottom: 4 }}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Icon key={i} name="star" size={13} />
                  ))}
                </span>
                <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.4 }}>“{t.quote}”</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ padding: '4px 9px', borderRadius: 999, background: GREEN_BG, color: GREEN_FG, fontSize: 11.5, fontWeight: 800 }}>{t.result}</span>
                  <span style={{ fontSize: 12.5, color: MUTED, fontWeight: 700 }}>{t.name} · {t.handle}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {shared.showcase ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={sectionTitle}>{shared.showcase.title}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(['before', 'after'] as const).map((side) => (
              <div key={side} style={{ position: 'relative', aspectRatio: '9/13', borderRadius: 18, overflow: 'hidden', background: INK }}>
                <img src={asset(shared.showcase![side])} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                <span
                  style={{
                    position: 'absolute',
                    left: 10,
                    top: 10,
                    padding: '4px 9px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 800,
                    background: side === 'after' ? GRAD : 'rgba(255,255,255,.92)',
                    color: side === 'after' ? '#fff' : INK,
                  }}
                >
                  {side === 'after' ? (shared.showcase!.afterLabel ?? 'After') : (shared.showcase!.beforeLabel ?? 'Before')}
                </span>
              </div>
            ))}
          </div>
          {shared.showcase.caption ? <div className="fk-copy" style={{ textAlign: 'center', fontSize: 12.5, color: MUTED, fontWeight: 600 }}>{shared.showcase.caption}</div> : null}
        </div>
      ) : null}

      <div style={{ padding: 16, borderRadius: 24, background: PAPER, boxShadow: SHADOW_CARD, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>{ctx.shared.ui?.everythingIncluded ?? 'Everything included'}</div>
        {shared.included.map((inc) => (
          <div key={inc} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14.5, fontWeight: 600 }}>
            <span style={{ width: 24, height: 24, flex: 'none', borderRadius: '50%', background: GRAD, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ph-bold ph-check" style={{ fontSize: 13 }} aria-hidden="true" />
            </span>
            {inc}
          </div>
        ))}
      </div>

      {shared.how?.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={sectionTitle}>{shared.howTitle ?? 'How it works'}</div>
          {shared.how.map((h, i) => (
            <div key={h.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 18, background: CREAM }}>
              <span style={{ width: 40, height: 40, flex: 'none', borderRadius: 13, background: PAPER, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: CORAL_DEEP, position: 'relative' }}>
                <Icon name={h.icon} size={22} />
                <span style={{ position: 'absolute', top: -6, left: -6, width: 18, height: 18, borderRadius: '50%', background: GRAD, color: '#fff', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
              </span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{h.title}</div>
                <div style={{ fontSize: 13.5, color: MUTED, fontWeight: 500 }}>{h.body}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {shared.guarantee ? (
        <div style={{ padding: '14px 16px', borderRadius: 20, background: GREEN_BG, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Icon name="shield-check" size={24} color={GREEN_FG} style={{ flex: 'none' }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{shared.guarantee.title}</div>
            <div style={{ fontSize: 13.5, color: GREEN_TEXT, fontWeight: 600 }}>{shared.guarantee.body}</div>
          </div>
        </div>
      ) : null}

      {shared.faq?.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={sectionTitle}>{shared.faqTitle ?? 'Questions'}</div>
          {shared.faq.map((f) => (
            <details key={f.q} style={{ padding: '12px 14px', borderRadius: 16, background: PAPER, border: `2px solid ${LINE}` }}>
              <summary style={{ fontWeight: 800, fontSize: 14.5, cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                {f.q}
                <Icon name="caret-down" size={16} color={MUTED} />
              </summary>
              <div style={{ fontSize: 13.5, color: MUTED, fontWeight: 500, marginTop: 8, lineHeight: 1.45 }}>{f.a}</div>
            </details>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'center', gap: 18, fontSize: 13, fontWeight: 700, color: MUTED, flexWrap: 'wrap' }}>
        {shared.trust
          .filter((label) => settings.features.moneyBackDays > 0 || !/money-back/i.test(label))
          .map((label, i) => (
            <span key={label} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <Icon name={i === 0 ? 'shield-check' : 'star'} size={18} color={ORANGE} />
              {/money-back/i.test(label) ? `${label} · ${settings.features.moneyBackDays} days` : label}
            </span>
          ))}
      </div>

      {shared.socialLine ? (
        <div style={{ textAlign: 'center', fontSize: 12.5, color: MUTED, fontWeight: 700 }}>{shared.socialLine}</div>
      ) : null}

      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 12, fontWeight: 700, paddingBottom: 8 }}>
        {[settings.app.restoreUrl, settings.app.termsUrl, settings.app.privacyUrl].map((href, i) => (
          <a key={href} href={href} target="_blank" rel="noreferrer" style={{ color: MUTED }}>
            {shared.links[i]}
          </a>
        ))}
      </div>
    </>
  );
}

const pick = <T extends object>(obj: T, keys: (keyof T)[]): Partial<T> =>
  Object.fromEntries(keys.filter((k) => obj[k] !== undefined).map((k) => [k, obj[k]])) as Partial<T>;

const sectionTitle: React.CSSProperties = { fontWeight: 800, fontSize: 18, letterSpacing: '-.02em', marginTop: 6 };

const digit: React.CSSProperties = {
  minWidth: 40,
  padding: '6px 6px',
  borderRadius: 10,
  background: 'rgba(255,255,255,.14)',
  fontSize: 22,
  fontWeight: 800,
  textAlign: 'center',
  lineHeight: 1,
};
